import { CommonModule } from '@angular/common'
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
} from '@angular/core'
import { LeafletDirective, LeafletModule } from '@bluehalo/ngx-leaflet'
import {
  divIcon,
  latLng,
  LatLngBounds,
  Layer,
  marker,
  Marker,
  tileLayer,
  TileLayer,
  Map as LeafletMap,
  Polyline,
  polyline,
  LatLngExpression,
  GeoJSON as LeafletGeoJSON,
  geoJSON,
} from 'leaflet'
import * as L from 'leaflet'

// Override Leaflet's default icon path
L.Icon.Default.imagePath = 'assets/images/'

// Import leaflet CSS directly if not already imported elsewhere
// This ensures the map and controls display properly
import 'leaflet/dist/leaflet.css'

export interface markerInputModel {
  coordinates: any
  iconColor?: string
  iconHtmlClass?: string
  tooltipText?: string
  highlighted?: boolean
  id?: string
  technicianInitials?: string // Add technician initials for enhanced markers
  markerType?: 'standard' | 'initials' | 'home' | 'hotel' | 'schedule' // Add marker type to differentiate display style
  scheduleTime?: string // Time to display for schedule markers (e.g., "09:30")
  scheduleOrder?: number // Order number in the daily schedule
  scheduleGroupedOrders?: string // For grouped markers: "1+2" format
  distanceFromAffairMin?: number // Distance in minutes from the affair being scheduled
  homeDepartureTime?: string // Departure time from home (e.g., "07:30") - for home markers
  homeArrivalTime?: string // Arrival time at first intervention (e.g., "08:00") - for home markers
  homeTravelDuration?: number // Travel duration in minutes from home to first intervention
  isHomeStart?: boolean // True if this is the start home marker (departure)
}

export interface RouteInputModel {
  coordinates: [number, number][] // Array of [lng, lat] coordinates
  color?: string
  weight?: number
  opacity?: number
  dashArray?: string
}

export interface DistanceLineInputModel {
  from: [number, number] // [lat, lng] - source point (affair)
  to: [number, number] // [lat, lng] - destination point (event)
  durationMin: number // Travel duration in minutes
  color?: string
}

export interface DepartmentBoundaryInputModel {
  departmentCode: string // Department code (e.g., "31", "09")
  color: string // Technician color for this department
  technicianInitials: string // Technician initials to display in tooltip
  technicianName: string // Full technician name
}

@Component({
  selector: 'app-map-with-markers',
  standalone: true,
  imports: [CommonModule, LeafletModule],
  templateUrl: './map-with-markers.component.html',
  styleUrl: './map-with-markers.component.scss',
})
export class MapWithMarkersComponent
  implements OnChanges, OnInit, AfterViewInit, OnDestroy
{
  private userHasMovedMap = false
  @Input({ required: true }) markers: markerInputModel[] = []
  /**
   * Optional "focus" coordinates.
   * When set, the map will center/zoom on this point instead of fitting bounds.
   */
  @Input() focusCoordinates: [number, number] | null = null // [lat, lng]
  @Input() focusZoom: number = 14
  @Input() route: RouteInputModel | null = null // Input for displaying a route polyline
  @Input() projectedRoute: RouteInputModel | null = null // Input for displaying a projected route (with affair)
  @Input() distanceLines: DistanceLineInputModel[] = [] // Distance lines from affair to events
  @Input() departmentBoundaries: DepartmentBoundaryInputModel[] = [] // Department boundaries with technician colors
  @Input() departmentGeoJson: any = null // GeoJSON data for departments
  @Input() styleClass: string = ''
  @Input() mapStyle: 'minimal' | 'standard' | 'light' | 'dark' = 'minimal'

  @ViewChild(LeafletDirective) leafletDirective!: LeafletDirective
  public map: LeafletMap | null = null

  public markerLayers: Layer[] = []
  public bounds: LatLngBounds = new LatLngBounds([])
  private routeLayer: Polyline | null = null // Store the route polyline layer
  private projectedRouteLayer: Polyline | null = null // Store the projected route polyline layer
  private distanceLineLayers: Layer[] = [] // Store distance line layers
  private departmentBoundaryLayer: LeafletGeoJSON | null = null // Store department boundary layer

  options = {
    layers: [
      // Default layer as fallback (will be replaced in ngOnInit)
      tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        minZoom: 3,
        attribution: '© OpenStreetMap contributors',
      }),
    ],
    zoom: 6,
    center: latLng(46.303558, 2.0164252),
    minZoom: 3,
  }

  // Flag to track if component is being destroyed
  private isDestroying = false
  private resizeObserver: ResizeObserver | null = null
  private mapContainer: HTMLElement | null = null

  constructor() {}

  ngOnInit() {
    // Set map tile layer based on selected style
    this.setMapTileLayer()
  }

  ngAfterViewInit() {
    // This ensures map is available after view init
    setTimeout(() => {
      if (this.leafletDirective) {
        this.map = this.leafletDirective.getMap()
        this.updateMarkerLayers()
        this.updateRouteLayer() // Also update route layer after map is ready
        this.updateProjectedRouteLayer() // Also update projected route layer after map is ready
        this.updateDistanceLines() // Update distance lines after map is ready
        this.updateDepartmentBoundaries() // Update department boundaries after map is ready
        this.refreshViewport()

        // Empêche le recentrage si l'utilisateur interagit avec la carte
        this.map.on('movestart zoomstart', () => {
          this.userHasMovedMap = true
        })

        // Get container and start observing resize
        this.mapContainer = this.map.getContainer()
        this.observeResize()
      }
    }, 100)
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If mapStyle changes, update the tile layer
    if (changes['mapStyle'] && !changes['mapStyle'].firstChange) {
      this.setMapTileLayer()
    }

    // If markers change, update the marker layers
    if (changes['markers']) {
      this.updateMarkerLayers()
    }

    // Focus can change independently of markers
    if (changes['focusCoordinates'] || changes['focusZoom']) {
      this.refreshViewport()
    }

    // If route changes, update the route layer
    if (changes['route']) {
      this.updateRouteLayer()
    }

    // If projected route changes, update the projected route layer
    if (changes['projectedRoute']) {
      this.updateProjectedRouteLayer()
    }

    // If distance lines change, update them
    if (changes['distanceLines']) {
      this.updateDistanceLines()
    }

    // If department boundaries or GeoJSON change, update boundaries
    if (changes['departmentBoundaries'] || changes['departmentGeoJson']) {
      this.updateDepartmentBoundaries()
    }
  }

  /**
   * Updates marker layers based on current markers
   */
  updateMarkerLayers() {
    this.bounds = new LatLngBounds([])
    this.markerLayers = []

    if (this.markers && this.markers.length > 0) {
      // Creation du layer pour les markers
      this.markers.forEach((marker) => {
        if (marker.coordinates && marker.coordinates.length === 2) {
          try {
            this.markerLayers.push(
              this.getMarker(marker, {
                iconColor: marker.iconColor,
                tooltips: marker.tooltipText,
                highlighted: marker.highlighted,
              })
            )
            this.bounds.extend(marker.coordinates)
          } catch (error) {
            console.error('Error creating marker:', error)
          }
        } else {
          console.warn('Invalid coordinates for marker:', marker)
        }
      })

      this.refreshViewport()
    }
  }

  /**
   * Updates the route layer based on current route data
   */
  updateRouteLayer() {
    // Remove existing route layer if it exists
    if (this.routeLayer && this.map) {
      this.map.removeLayer(this.routeLayer)
      this.routeLayer = null
    }

    // If no route data or no map, exit early
    if (
      !this.route ||
      !this.route.coordinates ||
      this.route.coordinates.length < 2 ||
      !this.map
    ) {
      return
    }

    // Convert coordinates from [lng, lat] to [lat, lng] for Leaflet
    const latLngs: LatLngExpression[] = this.route.coordinates.map(
      (coord) => [coord[1], coord[0]] as LatLngExpression
    )

    // Create the polyline with styling options
    this.routeLayer = polyline(latLngs, {
      color: this.route.color || '#3388ff',
      weight: this.route.weight || 4,
      opacity: this.route.opacity || 0.7,
      dashArray: this.route.dashArray || undefined,
    })

    // Add route to map
    this.routeLayer.addTo(this.map)

    // Extend bounds to include route
    const routeBounds = this.routeLayer.getBounds()
    if (routeBounds.isValid()) {
      if (this.bounds.isValid()) {
        this.bounds.extend(routeBounds)
      } else {
        this.bounds = routeBounds
      }
      this.fitBounds()
    }
  }

  /**
   * Updates the projected route layer based on projected route data
   * This shows the route including the affair being scheduled
   */
  updateProjectedRouteLayer() {
    // Remove existing projected route layer if it exists
    if (this.projectedRouteLayer && this.map) {
      this.map.removeLayer(this.projectedRouteLayer)
      this.projectedRouteLayer = null
    }

    // If no projected route data or no map, exit early
    if (
      !this.projectedRoute ||
      !this.projectedRoute.coordinates ||
      this.projectedRoute.coordinates.length < 2 ||
      !this.map
    ) {
      return
    }

    // Convert coordinates from [lng, lat] to [lat, lng] for Leaflet
    const latLngs: LatLngExpression[] = this.projectedRoute.coordinates.map(
      (coord) => [coord[1], coord[0]] as LatLngExpression
    )

    // Create the polyline with styling options - use dashed line by default
    this.projectedRouteLayer = polyline(latLngs, {
      color: this.projectedRoute.color || '#10B981', // Green color for projected route
      weight: this.projectedRoute.weight || 4,
      opacity: this.projectedRoute.opacity || 0.8,
      dashArray: this.projectedRoute.dashArray || '10, 10', // Dashed by default
    })

    // Add projected route to map
    this.projectedRouteLayer.addTo(this.map)

    // Extend bounds to include projected route
    const routeBounds = this.projectedRouteLayer.getBounds()
    if (routeBounds.isValid()) {
      if (this.bounds.isValid()) {
        this.bounds.extend(routeBounds)
      } else {
        this.bounds = routeBounds
      }
      this.fitBounds()
    }
  }

  /**
   * Updates distance lines from affair to each daily event
   */
  updateDistanceLines() {
    // Remove existing distance line layers
    if (this.map) {
      this.distanceLineLayers.forEach((layer) => {
        this.map!.removeLayer(layer)
      })
    }
    this.distanceLineLayers = []

    // If no distance lines or no map, exit early
    if (!this.distanceLines || this.distanceLines.length === 0 || !this.map) {
      return
    }

    // Create dashed polylines for each distance line
    this.distanceLines.forEach((line) => {
      if (!this.map) return

      // Skip lines where duration couldn't be calculated (0 or undefined)
      if (!line.durationMin || line.durationMin === 0) return

      const lineColor = line.color || '#3B82F6'

      // Create dashed polyline
      const dashedLine = polyline([line.from, line.to], {
        color: lineColor,
        weight: 2,
        opacity: 0.6,
        dashArray: '8, 8',
      })

      // Calculate midpoint for the label
      const midLat = (line.from[0] + line.to[0]) / 2
      const midLng = (line.from[1] + line.to[1]) / 2

      // Create a label marker at the midpoint
      const labelHtml = `<div class="distance-line-label">${Math.round(line.durationMin)} min</div>`
      const labelMarker = marker([midLat, midLng], {
        icon: divIcon({
          html: labelHtml,
          className: 'distance-line-label-wrapper',
          iconSize: [50, 20],
          iconAnchor: [25, 10],
        }),
        interactive: false,
      })

      // Add to map and store reference
      dashedLine.addTo(this.map)
      labelMarker.addTo(this.map)
      this.distanceLineLayers.push(dashedLine)
      this.distanceLineLayers.push(labelMarker)
    })
  }

  /**
   * Updates department boundary layers on the map
   * Displays department polygons colored by technician assignment
   */
  updateDepartmentBoundaries() {
    // Remove existing department boundary layer
    if (this.departmentBoundaryLayer && this.map) {
      this.map.removeLayer(this.departmentBoundaryLayer)
      this.departmentBoundaryLayer = null
    }

    // If no data or no map, exit early
    if (
      !this.departmentBoundaries ||
      this.departmentBoundaries.length === 0 ||
      !this.departmentGeoJson ||
      !this.map
    ) {
      return
    }

    // Build a lookup map: departmentCode -> boundary info (array, since multiple techs can share a department)
    const boundaryMap = new Map<string, DepartmentBoundaryInputModel[]>()
    this.departmentBoundaries.forEach((b) => {
      const existing = boundaryMap.get(b.departmentCode) || []
      existing.push(b)
      boundaryMap.set(b.departmentCode, existing)
    })

    // Filter GeoJSON features to only include departments that have technicians assigned
    const filteredGeoJson = {
      type: 'FeatureCollection' as const,
      features: this.departmentGeoJson.features.filter((feature: any) => {
        const code = feature.properties?.code
        return code && boundaryMap.has(code)
      }),
    }

    if (filteredGeoJson.features.length === 0) {
      return
    }

    // Create GeoJSON layer with styling
    this.departmentBoundaryLayer = geoJSON(filteredGeoJson as any, {
      style: (feature) => {
        const code = feature?.properties?.code
        const boundaries = code ? boundaryMap.get(code) : null
        // Unique technician names to detect shared departments
        const uniqueTechs = boundaries
          ? new Set(boundaries.map((b) => b.technicianName)).size
          : 0
        const isShared = uniqueTechs > 1
        const color = isShared ? '#9CA3AF' : boundaries?.[0]?.color || '#666'

        return {
          color: color,
          weight: 2,
          opacity: 0.8,
          fillColor: color,
          fillOpacity: isShared ? 0.08 : 0.12,
          dashArray: '5, 5',
        }
      },
      onEachFeature: (feature, layer) => {
        const code = feature.properties?.code
        const departmentName = feature.properties?.nom
        const boundaries = code ? boundaryMap.get(code) : null

        if (boundaries && boundaries.length > 0) {
          const techList = boundaries
            .map(
              (b) =>
                `<span style="color:${b.color}; font-weight:bold;">●</span> ${b.technicianName} (${b.technicianInitials})`
            )
            .join('<br/>')

          const tooltipContent = `<div class="department-boundary-tooltip">
            <strong>${departmentName} (${code})</strong><br/>
            ${techList}
          </div>`

          layer.bindTooltip(tooltipContent, {
            sticky: true,
            direction: 'top',
            opacity: 0.95,
          })
        }
      },
    })

    // Add to map (behind markers by using pane or just adding first)
    this.departmentBoundaryLayer.addTo(this.map)

    // Move to back so markers stay on top
    this.departmentBoundaryLayer.bringToBack()
  }

  /**
   * Sets the map tile layer based on the selected style
   */
  setMapTileLayer() {
    // Define the tile layer based on map style
    let newLayer

    switch (this.mapStyle) {
      case 'minimal':
        newLayer = tileLayer(
          'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
          {
            attribution: '© OpenStreetMap, © CartoDB',
            subdomains: 'abcd',
            maxZoom: 19,
          }
        )
        break
      case 'light':
        newLayer = tileLayer(
          'https://stamen-tiles-{s}.a.ssl.fastly.net/toner-lite/{z}/{x}/{y}{r}.png',
          {
            attribution: '© Stamen Design, © OpenStreetMap',
            subdomains: 'abcd',
            minZoom: 0,
            maxZoom: 20,
          }
        )
        break
      case 'dark':
        newLayer = tileLayer(
          'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
          {
            attribution: '© OpenStreetMap, © CartoDB',
            subdomains: 'abcd',
            maxZoom: 19,
          }
        )
        break
      case 'standard':
      default:
        newLayer = tileLayer(
          'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
          {
            attribution: '© OpenStreetMap contributors',
            maxZoom: 19,
          }
        )
        break
    }

    // Update options for new map instances
    this.options = {
      ...this.options,
      layers: [newLayer],
    }

    // If map is already initialized, update the layers
    if (this.map) {
      // Remove existing tile layers
      this.map.eachLayer((layer) => {
        if (layer instanceof L.TileLayer) {
          this.map?.removeLayer(layer)
        }
      })

      // Add new tile layer
      newLayer.addTo(this.map)
    }
  }

  getMarker(
    markerInput: markerInputModel,
    options: { iconColor?: string; tooltips?: string; highlighted?: boolean }
  ): Marker {
    const isHighlighted = options.highlighted
    const useInitials =
      markerInput.markerType === 'initials' && markerInput.technicianInitials
    const isHome = markerInput.markerType === 'home'
    const isHotel = markerInput.markerType === 'hotel'
    const isSchedule = markerInput.markerType === 'schedule'

    let html: string
    let className: string
    let size: [number, number]

    if (isSchedule) {
      // Create a schedule marker with time display
      const bgColor = options.iconColor ?? '#3B82F6'
      const time = markerInput.scheduleTime || '??:??'
      const order = markerInput.scheduleOrder ?? 0
      const groupedOrders = markerInput.scheduleGroupedOrders
      const isGrouped = !!groupedOrders
      const distanceMin = markerInput.distanceFromAffairMin

      // Adjust size based on content length
      const baseWidth = isGrouped
        ? Math.max(70, 40 + time.length * 5)
        : Math.max(50, 30 + time.length * 6)
      const markerWidth = isHighlighted ? baseWidth + 10 : baseWidth
      const markerHeight = isHighlighted ? 32 : 26

      const style = `--marker-bg: ${bgColor}; --marker-width: ${markerWidth}px; --marker-height: ${markerHeight}px;`

      // Distance badge HTML (shown below marker)
      const distanceBadge =
        distanceMin !== undefined && distanceMin !== null
          ? `<div class="schedule-distance-badge">${Math.round(distanceMin)}min</div>`
          : ''

      if (isGrouped) {
        // Grouped marker showing multiple orders
        html = `<div class="schedule-marker-content grouped" style="${style}">
          <span class="schedule-order grouped">${groupedOrders}</span>
          <span class="schedule-time">${time}</span>
        </div>${distanceBadge}`
      } else {
        html = `<div class="schedule-marker-content" style="${style}">
          <span class="schedule-order">${order}</span>
          <span class="schedule-time">${time}</span>
        </div>${distanceBadge}`
      }
      className = isHighlighted
        ? 'schedule-marker-wrapper highlighted-marker'
        : 'schedule-marker-wrapper'
      size = [markerWidth, markerHeight + (distanceMin !== undefined ? 14 : 0)]
    } else if (isHotel) {
      // Create a hotel marker with bed icon
      const bgColor = options.iconColor ?? '#3B82F6'
      const markerSize = isHighlighted ? 32 : 26
      const markerHeight = markerSize + 8
      const style = `--marker-bg: ${bgColor}; --marker-size: ${markerSize}px;`

      html = `<div class="hotel-marker-content" style="${style}">
        <div class="hotel-marker-icon">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M7 13c1.66 0 3-1.34 3-3S8.66 7 7 7s-3 1.34-3 3 1.34 3 3 3zm12-6h-8v7H3V5H1v15h2v-3h18v3h2v-9c0-2.21-1.79-4-4-4z"/>
          </svg>
        </div>
      </div>`
      className = isHighlighted
        ? 'hotel-marker-wrapper highlighted-marker'
        : 'hotel-marker-wrapper'
      size = [markerSize, markerHeight]
    } else if (isHome) {
      // Create a home marker with house icon and optional departure time
      const bgColor = options.iconColor ?? '#3B82F6'
      const markerSize = isHighlighted ? 32 : 26
      const departureTime = markerInput.homeDepartureTime
      const arrivalTime = markerInput.homeArrivalTime
      const travelDuration = markerInput.homeTravelDuration
      const isStartHome = markerInput.isHomeStart

      // Calculate marker width based on whether we have departure info
      const hasTimeInfo = isStartHome && departureTime
      const markerWidth = hasTimeInfo ? 85 : markerSize
      const markerHeight = hasTimeInfo ? 36 : markerSize + 8

      // Style for home marker
      const style = `--marker-bg: ${bgColor}; --marker-size: ${markerSize}px;`

      if (hasTimeInfo) {
        // Enhanced home marker with departure time display
        html = `<div class="home-marker-with-time" style="${style}">
          <div class="home-marker-icon-small">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
            </svg>
          </div>
          <div class="home-time-info">
            <span class="departure-time">${departureTime}</span>
            ${travelDuration ? `<span class="travel-duration">${travelDuration}min</span>` : ''}
          </div>
        </div>`
        className = isHighlighted
          ? 'home-marker-wrapper-time highlighted-marker'
          : 'home-marker-wrapper-time'
        size = [markerWidth, markerHeight]
      } else {
        // Simple circular home marker
        html = `<div class="home-marker-content" style="${style}">
          <div class="home-marker-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
            </svg>
          </div>
        </div>`
        className = isHighlighted
          ? 'home-marker-wrapper highlighted-marker'
          : 'home-marker-wrapper'
        size = [markerSize, markerHeight]
      }
    } else if (useInitials) {
      // Create a marker with technician initials
      const bgColor = options.iconColor ?? '#3B82F6'
      const initials = markerInput.technicianInitials || '??'
      const markerSize = isHighlighted ? 38 : 30

      html = `<div class="tech-marker-content">${initials}</div>`
      className = isHighlighted
        ? 'tech-marker-wrapper highlighted-marker'
        : 'tech-marker-wrapper'
      size = [markerSize, markerSize]

      // Set the CSS variables for this marker
      const style = `--marker-bg: ${bgColor}; --marker-size: ${markerSize}px;`

      html = `<div class="tech-marker-content" style="${style}">${initials}</div>`
    } else {
      // Standard map pin marker
      const bgColor = options.iconColor ?? '#e11d48'
      const markerSize = isHighlighted ? 32 : 27
      const innerSize = isHighlighted ? 13 : 10
      const glowSize = isHighlighted ? 10 : 6
      className = isHighlighted ? 'iconMarker highlighted-marker' : 'iconMarker'
      size = [markerSize, markerSize + 12]
      html = `<div class="affair-marker-content" style="--marker-bg: ${bgColor}; --marker-size: ${markerSize}px; --marker-inner-size: ${innerSize}px; --marker-glow-size: ${glowSize}px;">
        <span class="affair-marker-glow"></span>
        <span class="affair-marker-pin">
          <span class="affair-marker-core"></span>
        </span>
      </div>`
    }

    // Create the marker with appropriate options
    const zIndexOffset = isHighlighted
      ? 1000
      : isSchedule
        ? 600
        : useInitials
          ? 500
          : isHome
            ? 400
            : 0

    let myMarker: Marker = marker(markerInput.coordinates, {
      icon: divIcon({
        html: html,
        iconSize: size,
        className: className,
      }),
      zIndexOffset: zIndexOffset,
    })

    if (options.tooltips) {
      myMarker.bindTooltip(options.tooltips, {
        permanent: isHighlighted,
        direction: 'top',
        opacity: 0.9,
        offset: [0, -10],
      })

      if (isHighlighted) {
        setTimeout(() => {
          myMarker.openTooltip()
        }, 10)
      }
    }

    return myMarker
  }

  /**
   * Public method to force resize and refresh of the map when parent container changes
   * This should be called whenever the map container may have changed size
   */
  public invalidateSize(): void {
    setTimeout(() => {
      if (this.map) {
        // Force a complete invalidation and redraw
        this.map.invalidateSize(true)
        this.refreshViewport()
      }
    }, 100)
  }

  private refreshViewport() {
    if (!this.map) return

    // Ne recentre pas si l'utilisateur a déjà manipulé la carte
    if (this.userHasMovedMap) return

    if (this.focusCoordinates && this.focusCoordinates.length === 2) {
      try {
        this.map.setView(
          latLng(this.focusCoordinates[0], this.focusCoordinates[1]),
          this.focusZoom,
          { animate: true }
        )
      } catch {
        this.fitBounds()
      }
      return
    }

    this.fitBounds()
  }

  /**
   * Start observing container size changes
   */
  private observeResize(): void {
    if (!this.mapContainer || !window.ResizeObserver) return

    // Create a resize observer
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === this.mapContainer && this.map) {
          // When container size changes, force map to update
          this.map.invalidateSize(true)
        }
      }
    })

    // Start observing
    this.resizeObserver.observe(this.mapContainer)
  }

  fitBounds() {
    if (!this.map || !this.bounds.isValid()) {
      return
    }

    try {
      this.map.fitBounds(this.bounds, {
        padding: [30, 30],
        maxZoom: 15,
        animate: true,
      })
    } catch (error) {
      console.error('Error fitting bounds:', error)
    }
  }

  ngOnDestroy() {
    this.isDestroying = true

    // Clean up resize observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }

    // Completely detach event handling first
    try {
      if (this.map) {
        // Remove all event listeners
        this.map.off()
        this.map.eachLayer((layer) => {
          if (layer) {
            layer.off()
            this.map?.removeLayer(layer)
          }
        })
      }
    } catch (e) {
      // Silent catch - we're just trying to clean up as much as possible
    }

    // Now try to remove the map with additional safeguards
    setTimeout(() => {
      try {
        if (this.map) {
          // Only attempt to remove if the container exists and is in the DOM
          const container = this.map.getContainer()

          if (container && document.body.contains(container)) {
            // Check if map is already removed
            if (!this.isDestroying || this.map) {
              this.map.remove()
            }
          }

          // Release references
          this.map = null
          this.markerLayers = []
          this.routeLayer = null
          this.projectedRouteLayer = null
          this.departmentBoundaryLayer = null
          this.bounds = new LatLngBounds([])
        }
      } catch (error) {
        console.warn('Non-critical error during map cleanup:', error)
        // Last resort - simply nullify the reference
        this.map = null
      }
    }, 0)
  }
}
